import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";

// Vision UI components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";

// Vision UI layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

import { api } from "../../api";
import { IoListCircle, IoShieldCheckmark } from "react-icons/io5";

export default function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLogs = async () => {
    try {
      const res = await api.getActivity();
      if (res.success && res.data) {
        setLogs(res.data.activity);
      }
    } catch (err) {
      console.error("Failed to load activity logs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const getActionColor = (action) => {
    if (action.includes("CREATE") || action.includes("SUCCESS")) return "success";
    if (action.includes("TRIGGER") || action.includes("REDEPLOY")) return "info";
    if (action.includes("DELETE") || action.includes("DISCONNECT")) return "error";
    if (action.includes("ROLLBACK")) return "warning";
    return "dark";
  };

  const columns = [
    { name: "action", align: "left" },
    { name: "target", align: "left" },
    { name: "ip address", align: "center" },
    { name: "details", align: "left" },
    { name: "timestamp", align: "right" },
  ];

  const rows = logs.map((l) => ({
    action: (
      <VuiBadge
        color={getActionColor(l.action)}
        variant="standard"
        size="sm"
        badgeContent={l.action.replace(/_/g, " ")}
      />
    ),
    target: (
      <VuiTypography variant="caption" color="white" fontWeight="medium">
        {l.targetType ? `${l.targetType.toUpperCase()} #${l.targetId || ""}` : "-"}
      </VuiTypography>
    ),
    "ip address": (
      <VuiTypography variant="caption" color="text">
        {l.ipAddress || "127.0.0.1"}
      </VuiTypography>
    ),
    details: (
      <VuiTypography variant="caption" color="text" sx={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {Object.keys(l.details || {}).length > 0 ? JSON.stringify(l.details) : "-"}
      </VuiTypography>
    ),
    timestamp: (
      <VuiTypography variant="caption" color="text">
        {new Date(l.createdAt).toLocaleString()}
      </VuiTypography>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            Audit Activity & Logs
          </VuiTypography>
          <VuiTypography variant="button" color="text" fontWeight="regular">
            Tamper-evident audit trail of account actions, deployments, and security events
          </VuiTypography>
        </VuiBox>

        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiBox display="flex" alignItems="center" gap={1}>
              <IoShieldCheckmark color="#0075ff" size="20px" />
              <VuiTypography variant="lg" color="white" fontWeight="bold">
                Security & Operation Trail
              </VuiTypography>
            </VuiBox>
            <VuiButton variant="text" color="info" size="small" onClick={loadLogs}>
              Refresh
            </VuiButton>
          </VuiBox>

          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={8}>
              <CircularProgress color="info" />
            </VuiBox>
          ) : logs.length === 0 ? (
            <VuiBox py={8} textAlign="center">
              <IoListCircle size="48px" color="#0075ff" style={{ marginBottom: "16px" }} />
              <VuiTypography variant="h5" color="white" fontWeight="bold" mb={1}>
                No Activity Recorded
              </VuiTypography>
              <VuiTypography variant="button" color="text">
                Actions performed on your sites and deployments will appear here.
              </VuiTypography>
            </VuiBox>
          ) : (
            <Table columns={columns} rows={rows} />
          )}
        </Card>
      </VuiBox>

      <Footer />
    </DashboardLayout>
  );
}
